import type { UsersListResponse } from "@/types";

import { atom } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";

import { apiClient } from "@/utils/api";
import { buildApiParams } from "@/utils/helpers";
import { createPaginationParams, createTableQueryOptions } from "@/components/table";

// Shared params atom for users list
export type UsersListParams = {
  page: number;
  per_page: number;
  filters: Record<string, string | undefined>;
  refresh?: number;
};

export const usersListParamsAtom = atom<UsersListParams>({
  ...createPaginationParams(),
  filters: {},
});

// Hydration flag to delay initial query until URL/filters are synced
export const usersHydratedAtom = atom<boolean>(false);

// GET /users list query
export const usersQueryAtom = atomWithQuery<UsersListResponse>((get) => {
  const params = get(usersListParamsAtom);
  const hydrated = get(usersHydratedAtom);

  return createTableQueryOptions({
    queryKey: ["users", params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        { page: params.page, per_page: params.per_page },
        params.filters || {}
      );

      const { data } = await apiClient.get("/users", { params: apiParams });

      return data as UsersListResponse;
    },
    enabled: hydrated
  });
});

// Set user roles
export const setUserRoles = atom(
  null,
  async (get, set, { userId, roleIds }: { userId: number; roleIds: number[] }) => {
    await apiClient.post(`/users/${userId}/set-roles`, {
      role_ids: roleIds
    });

    // Trigger refresh by updating params
    const currentParams = get(usersListParamsAtom);
    set(usersListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

